import type { WorkPackage, WorkAuthority } from "./agentExecutionContract";

const VALID_AUTHORITIES = new Set<WorkAuthority>([
  "READ",
  "WORKSPACE_WRITE",
  "TEST_EXECUTION",
  "GIT_LOCAL",
  "GIT_REMOTE",
  "DATABASE",
  "INFRASTRUCTURE",
  "PRODUCTION",
]);

function requireNonEmptyString(
  value: unknown,
  field: string,
  workItemId: string,
): asserts value is string {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`INVALID_WORK_PACKAGE:${workItemId}:${field}`);
  }
}

function requireStringArray(value: unknown, field: string, workItemId: string) {
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) {
    throw new Error(`INVALID_WORK_PACKAGE:${workItemId}:${field}`);
  }
}

export function assertValidWorkPackage(
  value: unknown,
): asserts value is WorkPackage {
  if (!value || typeof value !== "object") {
    throw new Error("INVALID_WORK_PACKAGE:object-required");
  }

  const work = value as Partial<WorkPackage>;

  const id = typeof work.id === "string" ? work.id : "unknown";

  requireNonEmptyString(work.id, "id", id);

  requireNonEmptyString(work.phaseId, "phaseId", id);

  requireNonEmptyString(work.department, "department", id);

  requireNonEmptyString(work.agentRole, "agentRole", id);

  requireNonEmptyString(work.objective, "objective", id);

  requireStringArray(work.instructions, "instructions", id);

  requireStringArray(work.dependencies, "dependencies", id);

  requireStringArray(work.blocks, "blocks", id);

  requireStringArray(work.authorities, "authorities", id);

  requireStringArray(work.definitionOfDone, "definitionOfDone", id);

  if (typeof work.mayRunInParallel !== "boolean") {
    throw new Error(`INVALID_WORK_PACKAGE:${id}:mayRunInParallel`);
  }

  for (const authority of work.authorities ?? []) {
    if (!VALID_AUTHORITIES.has(authority as WorkAuthority)) {
      throw new Error(`INVALID_WORK_PACKAGE:${id}:authority:${authority}`);
    }
  }

  if (!work.resourceScope || typeof work.resourceScope !== "object") {
    throw new Error(`INVALID_WORK_PACKAGE:${id}:resourceScope`);
  }

  requireStringArray(work.resourceScope.paths, "resourceScope.paths", id);

  requireStringArray(work.resourceScope.services, "resourceScope.services", id);

  requireStringArray(
    work.resourceScope.integrations,
    "resourceScope.integrations",
    id,
  );
}

export function assertValidWorkPackages(
  values: readonly unknown[],
): asserts values is readonly WorkPackage[] {
  const ids = new Set<string>();

  for (const value of values) {
    assertValidWorkPackage(value);

    if (ids.has(value.id)) {
      throw new Error(`INVALID_WORK_PACKAGE:${value.id}:duplicate-id`);
    }

    ids.add(value.id);
  }

  /*
   * The first pass has runtime-validated every element.
   *
   * TypeScript cannot carry per-element narrowing from one independent
   * iteration over readonly unknown[] into a later loop, so establish the
   * validated collection boundary explicitly before dependency validation.
   */
  const validated = values as readonly WorkPackage[];

  for (const value of validated) {
    for (const dependency of value.dependencies) {
      if (!ids.has(dependency)) {
        throw new Error(
          `INVALID_WORK_PACKAGE:${value.id}:unknown-dependency:${dependency}`,
        );
      }
    }
  }
}
